import React from 'react';
import { ViewType, FormData } from '../types';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';

interface RegisterViewProps {
  formData: FormData;
  isLoading: boolean;
  error: string;
  success: string;
  onViewChange: (view: ViewType) => void;
  onInputChange: (field: keyof FormData, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onGoogleRegister?: () => void;
  onFacebookRegister?: () => void;
}

export const RegisterView: React.FC<RegisterViewProps> = ({
  formData,
  isLoading,
  error,
  success,
  onViewChange,
  onInputChange,
  onSubmit,
  onGoogleRegister,
  onFacebookRegister
}) => {
  const { t } = useTranslation();
  return (
    <div className="modal-content--full">
      <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
        <button className="text-slate-600 hover:text-slate-900 text-sm" onClick={() => onViewChange('welcome')} aria-label={t('common.back')}>← {t('common.back')}</button>
        <h2 className="text-lg font-semibold text-slate-900">{t('auth.createAccount')}</h2>
        <LanguageSwitcher />
      </div>
      <div className="px-6 py-6">
        <form onSubmit={onSubmit} className="form-container max-w-xl mx-auto">
          <div className="form-group">
            <label className="form-label">{t('auth.firstName')}</label>
            <input
              type="text"
              className="form-input"
              value={formData.firstName}
              onChange={(e) => onInputChange('firstName', e.target.value)}
              placeholder="Sarah"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('auth.lastName')}</label>
            <input
              type="text"
              className="form-input"
              value={formData.lastName}
              onChange={(e) => onInputChange('lastName', e.target.value)}
              placeholder="de Vries"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('auth.email')}</label>
            <input
              type="email"
              className="form-input"
              value={formData.email}
              onChange={(e) => onInputChange('email', e.target.value)}
              placeholder="sarah@example.com"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('auth.password')}</label>
            <input
              type="password"
              className="form-input"
              value={formData.password}
              onChange={(e) => onInputChange('password', e.target.value)}
              required
              minLength={8}
              placeholder={t('auth.passwordPlaceholder')}
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('auth.confirmPassword')}</label>
            <input
              type="password"
              className="form-input"
              value={formData.confirmPassword}
              onChange={(e) => onInputChange('confirmPassword', e.target.value)}
              required
              placeholder={t('auth.confirmPassword')}
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? t('auth.creatingAccount') : t('auth.createAccount')}
          </button>
          <div className="mt-4 space-y-2">
            <button
              type="button"
              onClick={onGoogleRegister}
              className="w-full inline-flex items-center justify-center gap-2 border border-gray-300 rounded-md py-2 px-3 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              aria-label="Google"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="h-5 w-5">
                <path fill="#EA4335" d="M24 9.5c3.5 0 6.7 1.2 9.2 3.6l6.2-6.2C34.9 3.1 29.7 1 24 1 14.6 1 6.7 6.6 3.1 14.3l7.7 6c1.8-5.5 7-10.8 13.2-10.8z"/>
                <path fill="#34A853" d="M46.5 24.5c0-1.6-.1-2.7-.4-3.9H24v7.3h12.7c-.6 3.5-2.6 6.4-5.6 8.4l8.5 6.6c5-4.6 7.9-11.4 7.9-18.4z"/>
                <path fill="#FBBC04" d="M10.8 28.3c-.4-1.2-.7-2.5-.7-3.8s.2-2.6.6-3.8l-7.7-6C1 17 1 20.5 1 24s0 7 2.1 9.7l7.7-5.4z"/>
                <path fill="#4285F4" d="M24 47c6.5 0 12-2.1 16-5.7l-8.5-6.6c-2.3 1.6-5.3 2.6-7.5 2.6-5.8 0-11.4-3.9-13.2-9.5l-7.7 5.4C6.7 41.4 14.6 47 24 47z"/>
              </svg>
              <span>{t('consumerRegister.registerWithGoogle')}</span>
            </button>
            <button
              type="button"
              onClick={onFacebookRegister}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md py-2 px-3 text-sm font-medium text-white hover:opacity-90"
              style={{ backgroundColor: '#1877F2' }}
              aria-label="Facebook"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
                <path d="M22 12.07C22 6.48 17.52 2 11.93 2S1.86 6.48 1.86 12.07c0 5.01 3.66 9.17 8.44 9.93v-7.02H7.9v-2.91h2.4V9.41c0-2.37 1.42-3.68 3.59-3.68 1.04 0 2.13.18 2.13.18v2.34h-1.2c-1.18 0-1.55.73-1.55 1.48v1.78h2.64l-.42 2.91h-2.22V22c4.78-.76 8.44-4.92 8.44-9.93z"/>
              </svg>
              <span>{t('consumerRegister.registerWithFacebook')}</span>
            </button>
          </div>
          <button 
            type="button"
            className="btn btn-link"
            onClick={() => onViewChange('login')}
          >
            {t('auth.hasAccountLogin')}
          </button>
        </form>
      </div>
    </div>
  );
};
